import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { hasModalBackground } from '../hooks/useSafeBackNavigation';

interface ModalOverlayContextValue {
    isOpen: boolean;
    isContentReady: boolean;
    markContentReady: () => void;
    markContentLoading: () => void;
}

const ModalOverlayContext = createContext<ModalOverlayContextValue>({
    isOpen: false,
    isContentReady: false,
    markContentReady: () => {},
    markContentLoading: () => {},
});

/** True when a modal route is open on top of a frozen background page (e.g. gradebook). */
export function ModalOverlayProvider({ children }: { children: React.ReactNode }) {
    const location = useLocation();
    const isOpen = hasModalBackground(location);
    const [isContentReady, setIsContentReady] = useState(false);

    useEffect(() => {
        setIsContentReady(false);
    }, [location.pathname, location.key]);

    const markContentReady = useCallback(() => setIsContentReady(true), []);
    const markContentLoading = useCallback(() => setIsContentReady(false), []);

    const value = useMemo(
        () => ({ isOpen, isContentReady, markContentReady, markContentLoading }),
        [isOpen, isContentReady, markContentReady, markContentLoading],
    );

    return (
        <ModalOverlayContext.Provider value={value}>
            {children}
        </ModalOverlayContext.Provider>
    );
}

export function useModalOverlay(): ModalOverlayContextValue {
    return useContext(ModalOverlayContext);
}

/** Backward-compatible helper — true when a modal route is open with a frozen background. */
export function useIsModalOverlayOpen(): boolean {
    return useContext(ModalOverlayContext).isOpen;
}

/** Call from modal route components once mounted so mission UI waits for the modal shell. */
export function useModalOverlayContentReady(): void {
    const { markContentReady, markContentLoading } = useModalOverlay();

    useEffect(() => {
        markContentReady();
        return () => markContentLoading();
    }, [markContentReady, markContentLoading]);
}
